import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import api from './api/axios';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import GenerateCode from './pages/GenerateCode';
import BuyCredits from './pages/BuyCredits';
import MyCodes from './pages/MyCodes';
import TrackOrder from './pages/TrackOrder';
import TrackOrderPT from './pages/TrackOrderPT';
import AdminDashboard from './pages/AdminDashboard';
import AdminUsers from './pages/AdminUsers';
import AdminSales from './pages/AdminSales';
// AdminCredits removido — funcionalidade integrada em /admin/users/:id (aba Créditos)
import AdminSettings from './pages/AdminSettings';
import AdminReports from './pages/AdminReports';
import AdminAuditLogs from './pages/AdminAuditLogs';
import AdminUserDetail from './pages/AdminUserDetail';
import AdminCodes from './pages/AdminCodes';
import ShopifyIntegration from './pages/ShopifyIntegration';
import CheckoutIntegrations from './pages/CheckoutIntegrations';
import NotificationIntegrations from './pages/NotificationIntegrations';
import AdminShopify from './pages/AdminShopify';
import AdminCheckouts from './pages/AdminCheckouts';
import ClientLayout from './components/ClientLayout';
import AdminLayout from './components/AdminLayout';

// Font Awesome for WhatsApp icon
const FontAwesomeLink = () => (
  <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
);

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="min-h-screen bg-gray-100 flex items-center justify-center">Carregando...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (adminOnly && !user.isAdmin) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

// WhatsApp Floating Button Component
const WhatsAppButton = () => {
  const location = useLocation();
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWhatsappNumber = async () => {
      try {
        const response = await api.get('/settings');
        if (response.data.whatsappNumber) {
          setWhatsappNumber(response.data.whatsappNumber);
        }
      } catch (error) {
        console.error('Error fetching WhatsApp number:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWhatsappNumber();
  }, []);

  // Don't show button if no number configured
  if (loading || !whatsappNumber) {
    return null;
  }

  // Hide on public tracking pages
  const path = location.pathname.toLowerCase();
  const isHidden = path === '/track' || path === '/rastreios';

  const preMessage = encodeURIComponent('Olá! Vim pelo site e gostaria de tirar uma dúvida.');

  return (
    <a
      href={`https://wa.me/${whatsappNumber}?text=${preMessage}`}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Falar no WhatsApp"
      className="wa-float"
      style={{ display: isHidden ? 'none' : 'flex' }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'scale(1.1)';
        e.currentTarget.style.boxShadow = '0 6px 30px rgba(37, 211, 102, 0.6)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.boxShadow = '0 4px 20px rgba(37, 211, 102, 0.4)';
      }}
    >
      <i className="fab fa-whatsapp"></i>
      <style>{`
        .wa-float {
          position: fixed;
          bottom: calc(20px + env(safe-area-inset-bottom, 0px));
          right: calc(20px + env(safe-area-inset-right, 0px));
          width: 60px;
          height: 60px;
          background-color: #25D366;
          border-radius: 50%;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 20px rgba(37, 211, 102, 0.4);
          z-index: 9999;
          text-decoration: none;
          animation: wa-pulse 2s infinite;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .wa-float i { font-size: 32px; color: #fff; }

        @keyframes wa-pulse {
          0% { box-shadow: 0 4px 20px rgba(37, 211, 102, 0.4); }
          50% { box-shadow: 0 4px 30px rgba(37, 211, 102, 0.7); }
          100% { box-shadow: 0 4px 20px rgba(37, 211, 102, 0.4); }
        }

        /* Mobile: menor + safe-area maior */
        @media (max-width: 768px) {
          .wa-float {
            width: 52px;
            height: 52px;
            bottom: calc(16px + env(safe-area-inset-bottom, 0px));
            right: calc(16px + env(safe-area-inset-right, 0px));
          }
          .wa-float i { font-size: 26px; }
        }

        /* Acessibilidade: respeita preferência de menos movimento */
        @media (prefers-reduced-motion: reduce) {
          .wa-float { animation: none; }
        }
      `}</style>
    </a>
  );
};

// Redirect tracking domains to correct pages and hide path from URL
const TRACKING_DOMAINS = ['globaldriveshipping.com', 'www.globaldriveshipping.com'];
const RASTREIOS_DOMAINS = ['unicargalogistica.com.br', 'www.unicargalogistica.com.br'];

const TrackingDomainRedirect = () => {
  const host = window.location.hostname.toLowerCase();
  if (TRACKING_DOMAINS.includes(host)) return <Navigate to="/track" replace />;
  if (RASTREIOS_DOMAINS.includes(host)) return <Navigate to="/rastreios" replace />;
  return <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <AuthProvider>
      <FontAwesomeLink />
      <Router>
        {/* WhatsApp Floating Button - Dynamic from API */}
        <WhatsAppButton />

        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<TrackingDomainRedirect />} />
          
          {/* Rotas do cliente — todas dentro do ClientLayout (sidebar esquerda) */}
          <Route element={<ProtectedRoute><ClientLayout /></ProtectedRoute>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/generate-code" element={<GenerateCode />} />
            <Route path="/buy-credits" element={<BuyCredits />} />
            <Route path="/my-codes" element={<MyCodes />} />
            <Route path="/integrations/shopify" element={<ShopifyIntegration />} />
            <Route path="/integrations/checkouts" element={<CheckoutIntegrations />} />
            <Route path="/integrations/notifications" element={<NotificationIntegrations />} />
          </Route>

          <Route path="/track" element={<TrackOrder />} />
          <Route path="/rastreios" element={<TrackOrderPT />} />

          {/* /admin/credits redireciona — funcionalidade integrada em /admin/users/:id (aba Créditos) */}
          <Route path="/admin/credits" element={<Navigate to="/admin/users" replace />} />

          {/* Rotas do admin — todas dentro do AdminLayout (sidebar esquerda), mesmo
              padrão do ClientLayout. Os cards de atalho do AdminDashboard foram
              migrados pra itens de navegação na sidebar. */}
          <Route element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/sales" element={<AdminSales />} />
            <Route path="/admin/settings" element={<AdminSettings />} />
            <Route path="/admin/reports" element={<AdminReports />} />
            <Route path="/admin/audit-logs" element={<AdminAuditLogs />} />
            <Route path="/admin/users/:id" element={<AdminUserDetail />} />
            <Route path="/admin/codes" element={<AdminCodes />} />
            <Route path="/admin/shopify" element={<AdminShopify />} />
            <Route path="/admin/checkouts" element={<AdminCheckouts />} />
          </Route>
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
